import {
  Autocomplete,
  Box,
  Divider as MuiDivider,
  InputAdornment,
  MenuItem,
  styled,
  TextField,
  Theme,
} from '@mui/material';
import { forwardRef } from 'react';
import { countries, popularCountries } from '../../labels';
import { Check } from '@mui/icons-material';
import { compact } from 'lodash';

export type CountrySelectorProps = {
  error?: {
    message?: string;
  };
  placeholder?: string;
  value?: string;
  onChange: (
    value: string | undefined,
    option: { label: string; value: string; code: string } | null,
  ) => void;
  name?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  mode?: 'name' | 'code';
  disableAutoFill?: boolean;
  className?: string;
  showPopularFirst?: boolean;
};

const Divider = styled(MuiDivider)(({ theme }: { theme: Theme }) => ({
  margin: theme.spacing(theme.spacingSizes.xs, 0),
}));

const Option = styled(MenuItem)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  justifyContent: 'space-between',
  gap: theme.spacing(theme.spacingSizes.xs),
}));

const CountrySelector = forwardRef<HTMLDivElement, CountrySelectorProps>(
  (
    {
      name,
      disabled,
      error,
      placeholder,
      value,
      onChange,
      fullWidth,
      mode = 'name',
      disableAutoFill,
      className,
      showPopularFirst = false,
    },
    ref,
  ) => {
    const countryList = showPopularFirst
      ? [
          ...compact(
            popularCountries.map((popularCountry) =>
              countries.find((country) => country.name === popularCountry),
            ),
          ),
          ...countries.filter(
            (country) => !popularCountries.includes(country.name),
          ),
        ]
      : countries;

    const countryOptions = countryList.map((country) => ({
      label: country.name,
      value: mode === 'name' ? country.name : country.code,
      code: country.code,
      isPopular: popularCountries.includes(country.name),
    }));

    const selectedCountry =
      countryOptions.find((country) => country.value === value) ?? null;

    return (
      <Autocomplete
        disabled={disabled}
        ref={ref}
        fullWidth={fullWidth}
        disablePortal
        autoComplete={!disableAutoFill}
        options={countryOptions}
        getOptionLabel={(option) => option.label}
        getOptionKey={(option) => option.value}
        groupBy={
          showPopularFirst
            ? (option) => (option.isPopular ? 'Popular' : 'Other')
            : undefined
        }
        className={className}
        renderInput={(params) => (
          <TextField
            {...params}
            slotProps={{
              input: {
                ...params.InputProps,
                startAdornment: selectedCountry ? (
                  <InputAdornment position="start">
                    <img
                      loading="lazy"
                      width="20"
                      srcSet={`https://flagcdn.com/w40/${selectedCountry.code.toLowerCase()}.png 2x`}
                      src={`https://flagcdn.com/w20/${selectedCountry.code.toLowerCase()}.png`}
                      alt={selectedCountry.label}
                    />
                  </InputAdornment>
                ) : null,
              },
            }}
            name={name}
            error={!!error}
            helperText={error?.message?.toString()}
            type="text"
            size="small"
            placeholder={placeholder}
          />
        )}
        value={selectedCountry}
        onChange={(_, newValue) => {
          onChange(newValue?.value ?? undefined, newValue ?? null);
        }}
        renderOption={(props, option) => {
          return (
            <Option {...props}>
              <Box>
                <img
                  loading="lazy"
                  width="20"
                  srcSet={`https://flagcdn.com/w40/${option.code.toLowerCase()}.png 2x`}
                  src={`https://flagcdn.com/w20/${option.code.toLowerCase()}.png`}
                  alt={option.label}
                />
              </Box>
              {option.label}
              {value && option.value === value ? (
                <Check fontSize="small" color="action" />
              ) : null}
            </Option>
          );
        }}
        renderGroup={
          showPopularFirst
            ? (params) => (
                <li key={params.key}>
                  <ul>{params.children}</ul>
                  {params.group === 'Popular' && <Divider />}
                </li>
              )
            : undefined
        }
      />
    );
  },
);

export default CountrySelector;
